import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { isDevAuthBypass, isMsalAuthRequired, ensureMsalInitialized } from './index';

interface CdxIdentity {
  email?: string;
  displayName?: string;
}

function readCdxIdentity(): CdxIdentity | null {
  if (typeof window === 'undefined') return null;
  const id = (window as unknown as { CDX_IDENTITY?: CdxIdentity }).CDX_IDENTITY;
  return id && id.email ? id : null;
}

/** True while the bootAuth inline script is still running (its overlay div lives in the DOM). */
function bootAuthStillRunning(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.getElementById('auth-bootstrap');
}

/**
 * Route guard for authenticated paths.
 * Per spec §8 — every PHI user authenticates via Entra B2B + MFA.
 *
 * Behavior:
 *  - Dev-mode bypass (no VITE_ENTRA_CLIENT_ID): always allow, so the UI loads
 *    without Azure setup
 *  - Prod: MSAL `useIsAuthenticated`; redirect to /login if not signed in
 */
export function AuthRoute() {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  // Track bootAuth state: it's an async <script> in index.html that runs in
  // parallel with React mount. If we redirect before it finishes we race past
  // a valid cookie session and dump the user on /login.
  const [identity, setIdentity] = useState<CdxIdentity | null>(() => readCdxIdentity());
  const [bootAuthRunning, setBootAuthRunning] = useState<boolean>(() => bootAuthStillRunning());

  useEffect(() => {
    ensureMsalInitialized().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('MSAL init failed', e);
    });
  }, [instance]);

  useEffect(() => {
    // Stop polling once we have an identity or bootAuth has clearly finished.
    if (identity || !bootAuthRunning) return;
    const interval = setInterval(() => {
      const id = readCdxIdentity();
      if (id) {
        setIdentity(id);
        setBootAuthRunning(false);
        clearInterval(interval);
      } else if (!bootAuthStillRunning()) {
        setBootAuthRunning(false);
        clearInterval(interval);
      }
    }, 100);
    // Safety stop after 8s — bootAuth itself errors out long before this.
    const safety = setTimeout(() => {
      setBootAuthRunning(false);
      clearInterval(interval);
    }, 8000);
    return () => {
      clearInterval(interval);
      clearTimeout(safety);
    };
  }, [identity, bootAuthRunning]);

  if (isDevAuthBypass()) {
    return <Outlet />;
  }

  // HMAC signed-URL path: cookie-backed session; let bootAuth complete first.
  if (bootAuthRunning) {
    return null; // bootAuth's overlay is covering the viewport
  }

  // Login wall (Stage 2, rev37+): when VITE_REQUIRE_MSAL_AUTH=true the
  // signed-link cookie alone is NOT enough — also require an MSAL session.
  // William: "all public cannot see or access the data at all without a
  // log-in and password (i.e., login into CDX)". The cookie still drives
  // per-recipient context (watermark, role); Entra is the actual auth gate.
  //
  // EXCEPTION — /print/* routes (Weekly Digest, etc.): MSAL caches in
  // sessionStorage (per-tab) by design for PHI cache discipline, so a new
  // browser tab opened via window.open never sees the MSAL session even
  // when the user is signed in on the originating tab. Print routes only
  // render what the API returns; the data API itself enforces cdx_session
  // + allowlist + audit on every fetch. Cookie identity is therefore
  // sufficient for print routes, and the gate cannot bounce the new tab.
  if (isMsalAuthRequired()) {
    const isPrintRoute = location.pathname.startsWith('/print/');
    if (isPrintRoute && identity) {
      return <Outlet />;
    }
    if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return <Outlet />;
  }

  // Stage 1 (wall flag not flipped yet): cookie alone is enough.
  if (identity) {
    return <Outlet />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
